"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TrackerCore = void 0;
var cuid_1 = __importDefault(require("cuid"));
var deep_diff_1 = require("deep-diff");
var cookies_js_1 = __importDefault(require("cookies-js"));
var schema_inspector_1 = __importDefault(require("schema-inspector"));
var utils_1 = require("./utils");
var version_1 = require("./version");
var amputils_1 = require("./amputils");
var eventTypes_1 = require("./eventTypes");
var constants_1 = require("./constants");
var DEPRECATED_EVENT_TYPES = [
    eventTypes_1.EVENT_TYPE_VIEW_CART,
    eventTypes_1.EVENT_TYPE_MORE_REFINEMENTS,
];
// Creates a message that can be shown to a GroupBy customer implementing beacons to tell them that the beacon type they
// are trying to send has been removed.
function eventTypeRemovedMsg(eventType) {
    return "The " + eventType + " event type has been removed. Calling this function results in no beacon being sent. The function is a no op with no performance implications. The code calling this function can be removed at a time it is convenient to do so.";
}
function TrackerCore(schemas, sanitizeEvent) {
    function TrackerCtr(customerId, area, options) {
        if (area === void 0) { area = 'Production'; }
        // Setting up customer
        if (typeof customerId !== 'string' || customerId.length === 0) {
            throw new Error('customerId must be a string with length');
        }
        var internals = {
            SET_FROM_COOKIES: 'setFromCookies',
            NOT_SET_FROM_COOKIES: 'notSetFromCookies',
            DEBUG_COOKIE_KEY: 'gbi_debug',
            SESSION_COOKIE_KEY: 'gbi_sessionId',
            VISITOR_COOKIE_KEY: 'gbi_visitorId',
            SESSION_TIMEOUT_SEC: 30 * 60,
            VERSION: version_1.version,
            VISITOR_TIMEOUT_SEC: 60 * 60 * 24 * 365 * 10,
            SCHEMAS: schemas,
            SANITIZE_EVENT: sanitizeEvent,
            WINDOW: window,
            COOKIES_LIB: cookies_js_1.default,
            CUSTOMER_ID: customerId,
            AREA: area,
            CUSTOMER: {
                id: customerId,
                area: area,
            },
            VISIT: {
                customerData: {},
            },
            INVALID_EVENT_CALLBACK: undefined,
            STRICT_MODE: false,
            WARNINGS_DISABLED: false,
            OVERRIDEN_PIXEL_URL: options === null || options === void 0 ? void 0 : options.overrideUrl,
            VISITOR_SETTINGS_SOURCE: undefined,
            IGNORED_FIELD_PREFIXES: [
                'search.records.[].allMeta',
                'search.template.zones',
            ],
            overrideCookiesLib: function (cookies) {
                internals.COOKIES_LIB = cookies;
            },
            overridePixelPath: function (path) {
                internals.OVERRIDEN_PIXEL_URL = path;
            },
            /**
             * Take event, convert to string, split by max length, and send along with uuid and customer info
             * @param event The event to send.
             */
            sendEvent: function (event) {
                var eventType;
                if (!event || !event.eventType) {
                    // This should never happen, but there's nothing we can do if it does happen, because the URL
                    // depends on the event type.
                    // No log message because we don't want to pollute logs of browser.
                    return;
                }
                eventType = event.eventType;
                if (DEPRECATED_EVENT_TYPES.indexOf(eventType) > -1) {
                    console.info(eventTypeRemovedMsg(eventType));
                    return;
                }
                if (internals.WINDOW.GROUPBY_BEACON_DEBUG || internals.COOKIES_LIB.get(internals.DEBUG_COOKIE_KEY)) {
                    console.log("Beaconing event: " + JSON.stringify(event, null, 2));
                }
                var protocol = internals.getProtocol(document);
                var host = protocol + "//" + customerId + ".groupbycloud.com";
                var path = "/wisdom/v2/pixel";
                var url;
                if (internals.OVERRIDEN_PIXEL_URL && (typeof internals.OVERRIDEN_PIXEL_URL === 'string') && internals.OVERRIDEN_PIXEL_URL.length > 0) {
                    url = internals.OVERRIDEN_PIXEL_URL;
                }
                else {
                    url = host + path;
                }
                var oReq = new XMLHttpRequest();
                oReq.open("POST", url);
                // Prevent the need for CORS pre-flight OPTIONS requests by overriding Content-Type from
                // application/json to text/plain. Server understands this was done and parses accordingly.
                oReq.setRequestHeader('Content-Type', 'text/plain');
                oReq.send(JSON.stringify(event));
            },
            /**
             * Helper to prepare, validate, and send event
             * @param event
             * @param eventType
             */
            prepareAndSendEvent: function (event, eventType) {
                var fullEvent = that.prepareEvent(event, eventType);
                var sanitizationSchema = internals.SCHEMAS[eventType];
                var validated = internals.validateEvent(fullEvent, sanitizationSchema || {});
                if (validated && validated.event) {
                    internals.sendEvent(validated.event);
                }
                else {
                    if (internals.INVALID_EVENT_CALLBACK) {
                        internals.INVALID_EVENT_CALLBACK(fullEvent, validated.error);
                    }
                }
            },
            /**
             * Based on the sanitization (and optionally, validation) schema
             * provided, sanitizes (and optionally, validates) an event for
             * sending to the tracker endpoint.
             * @param event
             * @param schema
             */
            validateEvent: function (event, schema) {
                var sanitizationSchema = schema;
                var validationSchema;
                // Only search events have validation too
                if (event.eventType && event.eventType === eventTypes_1.EVENT_TYPE_SEARCH) {
                    sanitizationSchema = schema.sanitization;
                    validationSchema = schema.validation;
                }
                var sanitizedEvent = utils_1.deepCopy(event);
                internals.SANITIZE_EVENT(sanitizedEvent, sanitizationSchema || {});
                if (validationSchema) {
                    var result = schema_inspector_1.default.validate(schema.validation || {}, sanitizedEvent);
                    if (!result.valid) {
                        console.error("error while processing event: " + result.format());
                        return {
                            event: undefined,
                            error: result.format(),
                        };
                    }
                }
                if (!sanitizedEvent.visit) {
                    sanitizedEvent.visit = {};
                }
                if (!sanitizedEvent.visit.generated) {
                    sanitizedEvent.visit.generated = {};
                }
                var removedFields = internals.getRemovedFields(sanitizedEvent, event);
                if (removedFields.length > 0) {
                    if (internals.STRICT_MODE) {
                        throw new Error("Unexpected fields " + JSON.stringify(removedFields) + " in eventType: " + sanitizedEvent.eventType);
                    }
                    if (!sanitizedEvent.metadata) {
                        sanitizedEvent.metadata = [];
                    }
                    for (var _i = 0, removedFields_1 = removedFields; _i < removedFields_1.length; _i++) {
                        var removedField = removedFields_1[_i];
                        if (!internals.WARNINGS_DISABLED) {
                            console.warn("unexpected field: " + removedField + " is being dropped from eventType: " + sanitizedEvent.eventType);
                        }
                        sanitizedEvent.metadata.push({
                            key: 'gbi-field-warning',
                            value: removedField,
                        });
                    }
                }
                sanitizedEvent.visit.generated.uri = (typeof internals.WINDOW !== 'undefined' && internals.WINDOW.location) ? internals.WINDOW.location.href : '';
                sanitizedEvent.visit.generated.timezoneOffset = new Date().getTimezoneOffset();
                sanitizedEvent.visit.generated.localTime = new Date().toISOString();
                if (document && document.referrer) {
                    sanitizedEvent.visit.generated.referer = document.referrer;
                }
                return { event: sanitizedEvent };
            },
            /**
             * Compared the sanitized event to the original, and return an object containing the properties that were removed.
             * @param sanitizedEvent
             * @param originalEvent
             * @returns {*}
             */
            getRemovedFields: function (sanitizedEvent, originalEvent) {
                var allDifferences = deep_diff_1.diff(sanitizedEvent, originalEvent);
                var removedFields = [];
                if (!allDifferences) {
                    return removedFields;
                }
                for (var _i = 0, allDifferences_1 = allDifferences; _i < allDifferences_1.length; _i++) {
                    var difference = allDifferences_1[_i];
                    if (!difference || !difference.path) {
                        continue;
                    }
                    for (var j = 0; j < difference.path.length; j++) {
                        if (typeof difference.path[j] === 'number') {
                            // Remove array indices
                            difference.path[j] = '[]';
                        }
                    }
                    if (difference.kind === 'N') {
                        if (!difference.path) {
                            continue;
                        }
                        var fieldName = difference.path.join('.');
                        if (!utils_1.startsWithOneOf(fieldName, internals.IGNORED_FIELD_PREFIXES)) {
                            removedFields.push(fieldName);
                        }
                    }
                }
                return utils_1.getUnique(removedFields);
            },
            getProtocol: function (document) {
                var protocol = document && document.location && document.location.protocol;
                // Only allowed protocols. Default to https
                if (protocol !== 'http:' && protocol !== 'https:') {
                    return 'https:';
                }
                return protocol;
            },
            /**
             * Clear metadata from items with service keys and add siteFilter item
             * @param metadata
             * @returns { Metadata | undefined }
             */
            getPreparedMetadata: function (metadata) {
                if (internals.SITE_FILTER == null) {
                    return metadata;
                }
                var siteFilterMetadataItem = {
                    key: constants_1.SITE_FILTER_METADATA_KEY,
                    value: internals.SITE_FILTER,
                };
                var filteredMetadata = (metadata || []).filter(function (_a) {
                    var key = _a.key;
                    return constants_1.METADATA_SERVICE_KEYS.indexOf(key.toLowerCase()) < 0;
                });
                filteredMetadata.push(siteFilterMetadataItem);
                return filteredMetadata;
            }
        };
        var that = {
            __getInternals: function () { return internals; },
            disableWarnings: function () {
                internals.WARNINGS_DISABLED = true;
            },
            enableWarnings: function () {
                internals.WARNINGS_DISABLED = false;
            },
            /**
             * Update visitor and session id's during login/logout
             * @param visitorId
             * @param sessionId
             */
            setVisitor: function (visitorId, sessionId) {
                console.warn('"setVisitor" is now deprecated and will be removed in a future major version of gb-tracker-client. Use "autoSetVisitor" instead.');
                if (internals.VISITOR_SETTINGS_SOURCE && internals.VISITOR_SETTINGS_SOURCE !== internals.NOT_SET_FROM_COOKIES) {
                    console.log('visitorId and sessionId already set using autoSetVisitor(). Ignoring setVisitor()');
                    return;
                }
                internals.VISITOR_SETTINGS_SOURCE = internals.NOT_SET_FROM_COOKIES;
                visitorId = (visitorId && typeof visitorId === 'number') ? ("" + visitorId) : visitorId;
                sessionId = (sessionId && typeof sessionId === 'number') ? ("" + sessionId) : sessionId;
                if (typeof visitorId !== 'string' || visitorId.length === 0) {
                    throw new Error('visitorId must be a string with length');
                }
                if (typeof sessionId !== 'string' || sessionId.length === 0) {
                    throw new Error('sessionId must be a string with length');
                }
                internals.VISIT.customerData.visitorId = visitorId;
                internals.VISIT.customerData.sessionId = sessionId;
            },
            /**
             * Initialize visitor data. Uses the following sources in this
             * order:
             * - AMP Linker param "gbi" (and sets this in 1st-party cookie)
             * - 1st-party cookies
             * - generated within this code (and sets this in 1st-party cookie)
             * @param loginId The optional login ID for the shopper.
             */
            autoSetVisitor: function (loginId) {
                if (internals.VISITOR_SETTINGS_SOURCE && internals.VISITOR_SETTINGS_SOURCE !== internals.SET_FROM_COOKIES) {
                    console.log('visitorId and sessionId already set using setVisitor(). Overriding setVisitor()');
                }
                if (loginId && typeof loginId !== 'string') {
                    throw new Error('if loginId is provided, it must be a string');
                }
                internals.VISITOR_SETTINGS_SOURCE = internals.SET_FROM_COOKIES;
                if (loginId && loginId.length > 0) {
                    internals.VISIT.customerData.loginId = loginId;
                }
                else {
                    delete internals.VISIT.customerData.loginId;
                }
                internals.VISIT.customerData.sessionId = internals.COOKIES_LIB.get(internals.SESSION_COOKIE_KEY);
                // Check for AMP Linker param for visitor ID:   
                internals.VISIT.customerData.visitorId = amputils_1.visitorIdFromAmpLinker(internals.WINDOW.document);
                var noVisitorSet = function () {
                    return !internals.VISIT.customerData.visitorId || internals.VISIT.customerData.visitorId.length < 1;
                };
                // Fall back to visitor ID from cookie if no visitor ID from AMP Linker.
                if (noVisitorSet()) {
                    internals.VISIT.customerData.visitorId = internals.COOKIES_LIB.get(internals.VISITOR_COOKIE_KEY);
                }
                if (!internals.VISIT.customerData.sessionId || internals.VISIT.customerData.sessionId.length < 1) {
                    internals.VISIT.customerData.sessionId = cuid_1.default();
                }
                internals.COOKIES_LIB.set(internals.SESSION_COOKIE_KEY, internals.VISIT.customerData.sessionId, { expires: internals.SESSION_TIMEOUT_SEC });
                // Fall back to visitor ID from new generated ID if no visitor ID from AMP Linker or cookie.
                if (noVisitorSet()) {
                    internals.VISIT.customerData.visitorId = cuid_1.default();
                }
                // Set cookie for visitor ID. This resets the expiry time if it
                // was a cookie already set before.
                var opts = {
                    expires: internals.VISITOR_TIMEOUT_SEC,
                };
                var isDomain = function (str) { return str.indexOf('.') >= 0; };
                if (internals.WINDOW.location
                    && internals.WINDOW.location.hostname
                    && internals.WINDOW.location.hostname.length > 0
                    && isDomain(internals.WINDOW.location.hostname)) {
                    opts.domain = utils_1.getApexDomain(internals.WINDOW);
                }
                internals.COOKIES_LIB.set(internals.VISITOR_COOKIE_KEY, internals.VISIT.customerData.visitorId, opts);
            },
            getVisitorId: function () {
                return internals.VISIT.customerData.visitorId;
            },
            getSessionId: function () {
                return internals.VISIT.customerData.sessionId;
            },
            getLoginId: function () {
                return internals.VISIT.customerData.loginId;
            },
            setStrictMode: function (strict) {
                internals.STRICT_MODE = strict;
            },
            /**
             * Append eventType, customer, visit, and siteFilter to event
             * @param event
             * @param type
             */
            prepareEvent: function (event, type) {
                // Continuously initialize visitor info in order to keep sessionId from expiring
                // Note that because we don't use sessionId anymore (and do sessioning by grouping events by event time
                // server side now), sessionId and things related to it will be removed in a future version.
                if (internals.VISITOR_SETTINGS_SOURCE === internals.SET_FROM_COOKIES) {
                    that.autoSetVisitor(internals.VISIT.customerData.loginId);
                }
                if (!internals.VISIT.customerData.sessionId || !internals.VISIT.customerData.visitorId) {
                    throw new Error('call autoSetVisitor() at least once before an event is sent');
                }
                var metadata = internals.getPreparedMetadata(event.metadata);
                var fullSendableEvent = __assign(__assign(__assign({}, event), { clientVersion: { raw: internals.VERSION }, eventType: type, customer: internals.CUSTOMER, visit: internals.VISIT }), (metadata && { metadata: metadata }));
                return fullSendableEvent;
            },
            /**
             * Set callback to be notified of invalid events
             * @param callback
             */
            setInvalidEventCallback: function (callback) {
                if (typeof callback !== 'function') {
                    throw new Error('invalid event callback must be a function');
                }
                internals.INVALID_EVENT_CALLBACK = callback;
            },
            /**
             * Validate and send addToCart event
             * @param event
             */
            sendAddToCartEvent: function (event) {
                internals.prepareAndSendEvent(event, eventTypes_1.EVENT_TYPE_ADD_TO_CART);
            },
            /**
             * Validate and send viewCart event
             * DEPRECATED. This code does nothing now.
             * @param event
             */
            sendViewCartEvent: function (event) {
                console.info(eventTypeRemovedMsg(eventTypes_1.EVENT_TYPE_VIEW_CART));
            },
            /**
             * Validate and send removeFromCart event
             * @param event
             */
            sendRemoveFromCartEvent: function (event) {
                internals.prepareAndSendEvent(event, eventTypes_1.EVENT_TYPE_REMOVE_FROM_CART);
            },
            /**
             * Validate and send order event
             * @param event
             */
            sendOrderEvent: function (event) {
                internals.prepareAndSendEvent(event, eventTypes_1.EVENT_TYPE_ORDER);
            },
            /**
             * Validate and send search event
             * @param event
             */
            sendSearchEvent: function (event) {
                internals.prepareAndSendEvent(event, eventTypes_1.EVENT_TYPE_SEARCH);
            },
            /**
             * Validate and send search event
             * @param event
             */
            sendAutoSearchEvent: function (event) {
                internals.prepareAndSendEvent(event, eventTypes_1.EVENT_TYPE_AUTO_SEARCH);
            },
            /**
             * Validate and send moreRefinements event.
             * DEPRECATED. This code does nothing now.
             * @param event
             */
            sendMoreRefinementsEvent: function (_) {
                console.info(eventTypeRemovedMsg(eventTypes_1.EVENT_TYPE_MORE_REFINEMENTS));
            },
            /**
             * Validate and send viewProduct event
             * @param event
             */
            sendViewProductEvent: function (event) {
                internals.prepareAndSendEvent(event, eventTypes_1.EVENT_TYPE_VIEW_PRODUCT);
            },
            /**
             * Validate and send Impression event
             * @param event
             */
            sendImpressionEvent: function (event) {
                internals.prepareAndSendEvent(event, eventTypes_1.EVENT_TYPE_IMPRESSION);
            },
            /**
             * Validate and send HomePageView event
             * @param event
             */
            sendHomePageViewEvent: function (event) {
                internals.prepareAndSendEvent(event, eventTypes_1.EVENT_TYPE_HOME_PAGE_VIEW);
            },
            /**
             * Initialize siteFilter parameter.
             * @param siteFilter
             */
            setSite: function (siteFilter) {
                if (siteFilter !== null && typeof siteFilter !== 'string') {
                    throw new Error('if siteFilter is provided, it must be a string or null');
                }
                internals.SITE_FILTER = siteFilter;
            }
        };
        return that;
    }
    TrackerCtr.VERSION = version_1.version;
    return TrackerCtr;
}
var TrackerCoreFinal = TrackerCore;
exports.TrackerCore = TrackerCoreFinal;
